import { useFetchLeadSourceOptions } from '@repo/config-services';
import { Select, type SelectProps } from 'antd';
import { useCallback, useMemo } from 'react';

export interface LeadSourceSelectProps extends Omit<SelectProps, 'options' | 'onFocus'> {
  value?: string;
  onChange?: (value: string) => void;
  withAnyOption?: boolean;
  changeValue?: 'id' | 'label';
}

export default function LeadSourceSelect({
  value,
  onChange,
  withAnyOption = false,
  changeValue = 'id',
  ...props
}: LeadSourceSelectProps) {
  const { leadSourceOptions: data, isLoading } = useFetchLeadSourceOptions();

  const options = useMemo(
    () => data?.leadAttributeOptions?.map(({ value: val, label }) => ({
      value: val,
      label,
    })) || [],
    [data],
  );

  const finalOptions = useMemo(() => {
    const preparedOptions = [...options];

    if (withAnyOption) {
      preparedOptions.unshift({
        value: 'any',
        label: 'Any',
      });
    }

    return preparedOptions;
  }, [options, withAnyOption]);

  const handleChange = useCallback((changedValue: string) => {
    if (changeValue === 'label') {
      const foundOption = options.find((opt) => opt.value === changedValue);
      if (foundOption) {
        onChange?.(foundOption.label);
      }
    } else {
      onChange?.(changedValue);
    }
  }, [onChange, changeValue, options]);

  return (
    <Select
      value={value}
      onChange={handleChange}
      options={finalOptions}
      loading={isLoading}
      defaultActiveFirstOption
      {...props}
    />
  );
}
